"use client";

import React, { useEffect, useState } from "react";
import { useCart } from "@/redux/features/cart/hooks";
import { Close, KeyboardArrowLeft, KeyboardArrowRight, ShoppingCartOutlined } from "@mui/icons-material";
import {
  Backdrop,
  Badge,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Popover,
  Stack,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  styled,
  BadgeProps,
  CardHeader,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import CartItemCard from "@/app/(main)/_components/CartItemCard";
import getError from "@/lib/getError";
import Link from "next/link";
import { usePathname } from "next/navigation";
import ShoppingBagIcon from "@/app/_components/Icons/ShoppingBagIcon";
import HoverPopover from "material-ui-popup-state/HoverPopover";
import { usePopupState, bindHover, bindPopover } from "material-ui-popup-state/hooks";
import Image from "next/image";

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  "& .MuiBadge-badge": {
    bottom: -8,
    border: `1px solid ${theme.palette.background.paper}`,
    padding: "4px 8px",
    minWidth: "auto",
    color: "white",
    borderRadius: 2,
    left: "50%",
    height: "16px",
  },
}));

export default function HeaderCartButton() {
  const [badgeClass, setBadgeClass] = useState("");

  const popupState = usePopupState({
    variant: "popover",
    popupId: "demoPopover",
  });
  const theme = useTheme();
  const { loading, itemsCount, cart, error } = useCart();
  const pathname = usePathname();
  useEffect(() => {
    if (itemsCount && itemsCount > 0) {
      setBadgeClass("badge-animation");
      const timer = setTimeout(() => {
        setBadgeClass("");
      }, 300); // Duration should match CSS animation duration

      return () => clearTimeout(timer);
    }
  }, [itemsCount]);
  const hasItems = Boolean(cart && cart.cartItems.length > 0);
  if (pathname.startsWith("/cart")) {
    return <></>;
  }

  return (
    <>
      <LoadingButton
        className={badgeClass}
        aria-label="shopping bag"
        variant="contained"
        disableElevation
        loading={loading}
        sx={{ minWidth: "auto", p: 1, borderRadius: 2 }}
        {...bindHover(popupState)}
      >
        <StyledBadge
          badgeContent={itemsCount}
          max={99}
          color="secondary"
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <ShoppingBagIcon />
        </StyledBadge>
      </LoadingButton>

      <HoverPopover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              boxShadow: `3px 6px 24px -6px #A1A1A129,-3px 0px 24px -6px #A1A1A11F`,
              borderRadius: 3,
              minWidth: {
                xs: hasItems ? "200px" : "300px",
                md: hasItems ? "200px" : "300px",
              },
              maxWidth: {
                xs: hasItems ? "330px" : "300px",
                md: hasItems ? "330px" : "300px",
              },
            },
          },
        }}
      >
        <Card variant="elevation" elevation={0} sx={{ borderRadius: 3, p: 2, position: "relative" }}>
          <Backdrop sx={{ color: "#fff", position: "absolute", zIndex: 2, borderRadius: 2 }} open={loading}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <Stack spacing={1}>
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
              <Typography variant="subtitle1">سبد خرید</Typography>
              {Boolean(itemsCount && itemsCount > 0) && <Typography variant="caption">{itemsCount} کالا</Typography>}
            </Stack>
            <Divider flexItem />
          </Stack>
          {!hasItems ? (
            <>
              <Box
                sx={{
                  mt: 1,
                  width: "100%",
                  // height: minHeight,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ position: "relative", width: "70%", aspectRatio: 1 }}>
                  <Image src="/errors/emptyBasket.svg" alt="emptyBasket" fill style={{ objectFit: "contain" }} />
                </Box>
                <Typography variant="subtitle1">سبد خرید شما خالی است!</Typography>
              </Box>
            </>
          ) : (
            <Grid container>
              <Grid item xs={12}>
                <Stack spacing={0} display={"flex"} flexDirection={"column"}>
                  <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center"></Stack>
                  <Box
                    sx={{ maxHeight: 81 * 5, overflow: "auto", display: "flex", flexDirection: "column", gap: 2, py: 1 }}
                    className="custom-scrollbar"
                  >
                    {cart &&
                      cart.cartItems.map((item, index) => (
                        <Box key={item.id}>
                          <CartItemCard item={item} />
                          {index < cart.cartItems.length - 1 && <Divider sx={{ mt: 2 }} />}
                        </Box>
                      ))}
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              {Boolean(itemsCount && itemsCount > 0) && (
                <Grid item xs={12} mt={2}>
                  <Stack spacing={2} display={"flex"} direction={"row"} alignItems={"center"} justifyContent={"space-between"}>
                    <Stack spacing={0} alignItems="start">
                      <Typography variant="body2" color={"grey.500"}>
                        مبلغ قابل پرداخت
                      </Typography>
                      <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={1}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          {cart?.totalPrice.toLocaleString()}
                        </Typography>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: "grey.600" }}>
                          تومان
                        </Typography>
                      </Stack>
                    </Stack>
                    <Button variant="contained" disableElevation sx={{ borderRadius: 98 }} size="large" href="/cart" component={Link}>
                      ثبت سفارش
                    </Button>
                  </Stack>
                </Grid>
              )}
            </Grid>
          )}
        </Card>
      </HoverPopover>
    </>
  );
}
